import { useMemo, useState } from 'react';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import type {
    LeadsBase, LeadsMelhorPiorDia, LeadsSerieDia, LeadsSerieHoraria,
} from '@/types/leadsDashboard';
import { diaCurto, horaCurta, n, pct } from '../format';

/**
 * Bloco 5.3 — Entrada e perda de leads.
 *
 * Gráfico divergente: barra para cima é captação, barra para baixo é perda por
 * 551, linha é o saldo líquido. A linha de saldo só aparece no modo por dia.
 */

/**
 * As faixas empilhadas são EXCLUSIVAS, e mostrá-las cruas engana.
 *
 * A faixa vermelha vale 7.335, mas o broadcast perdeu 13.457 — os outros
 * 6.122 estão na faixa roxa, porque essas pessoas também se perderam no flow.
 * Rotular a faixa de "Perdidos no broadcast" fazia o tooltip contradizer o
 * card, que mostra o total do canal.
 *
 * Mas mostrar o total de cada canal junto do total geral produz um tooltip em
 * que os subitens somam MAIS que o pai (13.457 + 6.588 contra 13.923), e isso
 * é ilegível por mais correto que seja.
 *
 * Então o tooltip lista exatamente as três faixas que a barra desenha, nessa
 * ordem, e elas SOMAM o total: 7.335 + 6.122 + 464 = 13.923. O total por canal
 * continua no card, que é onde essa pergunta é feita.
 */
/**
 * "Nos dois canais" NÃO é um canal — foi uma categoria que eu inventei e que
 * não existe no mundo. Só há flow e broadcast.
 *
 * O fundo do problema: o canal não é ONDE o lead se perdeu, é onde a gente
 * DESCOBRIU. A pessoa fechou a janela de 24h; o broadcast tentou e tomou 551,
 * o flow tentou e tomou 551. Uma perda, dois detectores — por isso a soma dos
 * canais nunca fecha com o total, e nenhuma fatia empilhada conserta isso.
 *
 * O gráfico volta a mostrar a PERDA, que é um número só. Onde foi detectada
 * fica no card, que não precisa somar.
 */
function Tip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div className="tt">
            <div className="tt-t">{label}</div>
            {payload
                .filter((p: any) => p.value != null)
                .map((p: any) => {
                    // `perdidos` é plotado NEGATIVO só para a barra descer —
                    // no texto ele é uma contagem, então vai sem sinal.
                    //
                    // `saldo` é diferente: o sinal é a informação. Aplicar
                    // Math.abs nele fazia 9.858 captados contra 26.356
                    // perdidos aparecer como "saldo +16.498" em verde, quando
                    // o dia perdeu 16.498 pessoas. Dia ruim lido como bom.
                    const ehSaldo = p.dataKey === 'saldo';
                    const v = ehSaldo ? p.value : Math.abs(p.value);
                    const cor = ehSaldo && v < 0 ? 'var(--red)' : undefined;
                    return (
                        <div className="tt-r" key={p.dataKey}>
                            <span style={{ color: p.color }}>{p.name}</span>
                            <span className="num" style={{ fontWeight: 600, color: cor }}>
                                {ehSaldo && v > 0 ? '+' : ''}{n(v)}
                            </span>
                        </div>
                    );
                })}
        </div>
    );
}

interface Props {
    serieDiaria: LeadsSerieDia[];
    serieHoraria: LeadsSerieHoraria;
    melhorPior: LeadsMelhorPiorDia;
    base: LeadsBase;
    rotulo: string;
    dias: number;
    nomePais?: string;
    nomePagina?: string;
}

export default function GraficoEntradaPerda({
    serieDiaria, serieHoraria, melhorPior, base, rotulo, dias, nomePais, nomePagina,
}: Props) {
    const [granul, setGranul] = useState<'hora' | 'dia'>('hora');

    const dados = useMemo(() => {
        if (granul === 'hora') {
            return serieHoraria.itens.map(i => ({
                rotulo: horaCurta(i.hora),
                // §4.1: hora futura fica VAZIA (null), nunca zero — plotar zero
                // faz o gráfico parecer que a captação parou.
                captados: i.captados,
                perdidos: i.perdidos_551 == null ? null : -i.perdidos_551,
                parcial: false,
            }));
        }
        return serieDiaria.map(d => ({
            rotulo: diaCurto(d.data),
            captados: d.captados,
            perdidos: -d.perdidos_551,
            saldo: d.saldo,
            parcial: d.parcial,
        }));
    }, [granul, serieDiaria, serieHoraria]);

    const legendaHora = serieHoraria.tipo === 'dia_unico'
        ? 'do dia'
        : `média por hora dos ${dias} dias`;

    const horasOcorridas = serieHoraria.itens.filter(i => i.captados != null).length || 1;
    const mediaPorHora = base.captados / horasOcorridas;
    const mediaPorDia = base.captados / (dias || 1);

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Entrada e perda de leads</div>
                    <div className="card-s">
                        {granul === 'hora' ? `Por hora ${legendaHora}` : 'Por dia no período'}
                        {' · barra para cima é captação, para baixo é erro 551'}
                        {nomePais && ` · ${nomePais}`}
                        {nomePagina && ` · ${nomePagina}`}
                    </div>
                </div>
                <div className="seg">
                    {([['hora', 'Por hora'], ['dia', 'Por dia']] as const).map(([k, l]) => (
                        <button
                            key={k} type="button"
                            data-on={granul === k ? 1 : 0}
                            onClick={() => setGranul(k)}
                        >
                            {l}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{ padding: '18px 12px 8px 0' }}>
                <ResponsiveContainer width="100%" height={272}>
                    <ComposedChart data={dados} margin={{ top: 4, right: 16, left: 8, bottom: 0 }} stackOffset="sign">
                        <CartesianGrid stroke="#F0F2F5" vertical={false} />
                        <XAxis
                            dataKey="rotulo"
                            tick={{ fontSize: 11, fill: '#8C93A0' }}
                            axisLine={{ stroke: '#E3E6EB' }}
                            tickLine={false}
                            interval={granul === 'hora' ? 1 : dias > 14 ? Math.floor(dias / 12) : 0}
                        />
                        <YAxis
                            tick={{ fontSize: 11, fill: '#8C93A0' }}
                            axisLine={false} tickLine={false} width={54}
                            tickFormatter={(v: number) =>
                                Math.abs(v) >= 1000 ? `${Math.round(Math.abs(v) / 1000)}k` : String(Math.abs(v))
                            }
                        />
                        <Tooltip content={<Tip />} cursor={{ fill: 'rgba(47,91,234,.05)' }} />
                        <ReferenceLine y={0} stroke="#C9CFD8" />
                        <Bar dataKey="captados" name="Captados" stackId="a" radius={[3, 3, 0, 0]} maxBarSize={40}>
                            {dados.map((d, i) => (
                                // §4.1: a barra do dia corrente usa tom mais claro.
                                <Cell key={i} fill={d.parcial ? '#7C9BF2' : '#2F5BEA'} />
                            ))}
                        </Bar>
                        <Bar dataKey="perdidos" name="Perdidos (551)" stackId="a" radius={[0, 0, 3, 3]} maxBarSize={40} fill="#C0392B" />
                        {granul === 'dia' && (
                            <Line type="monotone" dataKey="saldo" name="Saldo líquido" stroke="#12805C" strokeWidth={2} dot={false} />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* §5.3: rodapé só com dado que NÃO está nos cards do topo. */}
            <div className="strip">
                <div>
                    <span>Média por {granul === 'hora' ? 'hora' : 'dia'}</span>
                    <em>{granul === 'hora' ? (serieHoraria.tipo === 'dia_unico' ? 'no dia' : 'no período') : 'no período'}</em>
                    <b className="num">{n(granul === 'hora' ? mediaPorHora : mediaPorDia)}</b>
                </div>
                <div>
                    <span>Hora de pico</span>
                    <em>curva do período</em>
                    <b className="num">
                        {serieHoraria.hora_pico != null ? horaCurta(serieHoraria.hora_pico) : '–'}
                    </b>
                </div>
                <div>
                    <span>Melhor dia</span>
                    <em>mês de {melhorPior.mes}</em>
                    <b className="num">
                        {melhorPior.melhor
                            ? `${diaCurto(melhorPior.melhor.data)} · ${n(melhorPior.melhor.captados)}`
                            : '–'}
                    </b>
                </div>
                <div>
                    <span>Pior dia</span>
                    <em>mês de {melhorPior.mes}</em>
                    <b className="num muted">
                        {melhorPior.pior
                            ? `${diaCurto(melhorPior.pior.data)} · ${n(melhorPior.pior.captados)}`
                            : '–'}
                    </b>
                </div>
                <div>
                    <span>551 sobre captação</span>
                    <em>{rotulo}</em>
                    <b className="num" style={{ color: 'var(--red)' }}>
                        {pct(base.perdidos_sobre_captados_pct, 0)}
                    </b>
                </div>
            </div>
        </div>
    );
}
